import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function Chat() {
  const [params] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [documentIds] = useState(params.get('doc') ? [params.get('doc')] : null);
  const [conversations, setConversations] = useState([]);
  const [showSidebar, setShowSidebar] = useState(true);
  const [expandedSources, setExpandedSources] = useState(new Set());
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetch('/api/conversations').then(r => r.ok ? r.json() : {}).then(d => setConversations(Array.isArray(d?.conversations) ? d.conversations : [])).catch(() => {});
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadConv = useCallback(async (cid) => {
    try {
      const r = await fetch(`/api/conversations/${cid}`);
      const d = await r.json();
      setConversationId(cid);
      setMessages(d.messages || []);
    } catch {}
  }, []);

  const deleteConv = useCallback(async (cid) => {
    await fetch(`/api/conversations/${cid}`, { method: 'DELETE' });
    setConversations(prev => prev.filter(c => c.id !== cid));
    if (conversationId === cid) { setConversationId(null); setMessages([]); }
  }, [conversationId]);

  const send = useCallback(async () => {
    if (!input.trim() || sending) return;
    const q = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: q }]);
    setSending(true);
    try {
      const r = await fetch('/api/chat', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, conversation_id: conversationId, document_ids: documentIds }),
      });
      const d = await r.json();
      if (d.conversation_id) setConversationId(d.conversation_id);
      setMessages(prev => [...prev, { role: 'assistant', content: d.answer, source_chunks: d.sources }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Fehler: ' + e.message }]);
    }
    setSending(false);
    inputRef.current?.focus();
  }, [input, sending, conversationId, documentIds]);

  const newChat = () => { setConversationId(null); setMessages([]); };

  const toggleSource = (id) => setExpandedSources(prev => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Sidebar */}
      {showSidebar && (
        <div style={{ width: 220, minWidth: 220, borderRight: '1px solid var(--border)', padding: 'var(--gap-md)', overflow: 'auto' }}>
          <button onClick={newChat} style={{ width: '100%', background: '#bb9af7', border: 'none', borderRadius: 'var(--border-radius)', padding: '8px 12px', color: '#1a1b26', fontWeight: 600, cursor: 'pointer', fontSize: 'var(--font-size-sm)', marginBottom: 12 }}>Neuer Chat</button>
          {conversations.map(c => (
            <div key={c.id} onClick={() => loadConv(c.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 'var(--border-radius)', cursor: 'pointer', background: conversationId === c.id ? 'var(--bg-tertiary)' : 'transparent', marginBottom: 2 }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{c.title || 'Chat'}</span>
              <button onClick={e => { e.stopPropagation(); deleteConv(c.id); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, opacity: 0.6 }}>{'\u2715'}</button>
            </div>
          ))}
        </div>
      )}

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '8px var(--gap-md)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setShowSidebar(!showSidebar)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}>{showSidebar ? '\u25C0' : '\u25B6'}</button>
          <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 'var(--font-size-base)' }}>Dokument-Chat</span>
          {documentIds && <span style={{ background: 'rgba(187,154,247,0.15)', color: '#bb9af7', borderRadius: 4, padding: '2px 8px', fontSize: 11 }}>Filter: {documentIds.length} Dok.</span>}
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--gap-md)' }}>
          {messages.length === 0 && <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: 60 }}>Frage stellen, um zu starten</div>}
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
              <div style={{ maxWidth: '75%', background: m.role === 'user' ? 'rgba(187,154,247,0.15)' : 'var(--bg-secondary)', border: `1px solid ${m.role === 'user' ? 'rgba(187,154,247,0.3)' : 'var(--border)'}`, borderRadius: 'var(--border-radius-lg)', padding: '12px 16px' }}>
                <div style={{ color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{m.content}</div>
                {m.source_chunks?.length > 0 && (
                  <div style={{ marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                    <button onClick={() => toggleSource(i)} style={{ background: 'none', border: 'none', color: '#bb9af7', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>
                      {expandedSources.has(i) ? '\u25BC' : '\u25B6'} {m.source_chunks.length} Quellen
                    </button>
                    {expandedSources.has(i) && m.source_chunks.map((s, j) => (
                      <div key={j} style={{ background: 'var(--bg-tertiary)', borderRadius: 'var(--border-radius)', padding: 8, marginTop: 4, fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>
                        <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{s.document_title || s.filename} {s.page_number ? `(S. ${s.page_number})` : ''}</div>
                        {s.similarity != null && <span style={{ color: s.similarity > 0.7 ? '#9ece6a' : '#e0af68', marginLeft: 8 }}>{Math.round(s.similarity * 100)}%</span>}
                        <div style={{ marginTop: 4 }}>{(s.content_preview || '').slice(0, 200)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {sending && <div style={{ textAlign: 'center', color: '#bb9af7', fontSize: 'var(--font-size-sm)' }}>Denkt nach...</div>}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <div style={{ padding: 'var(--gap-md)', borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
          <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Frage stellen..." rows={1} style={{ flex: 1, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '10px 14px', color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)', resize: 'none', outline: 'none', fontFamily: 'inherit' }} />
          <button onClick={send} disabled={sending || !input.trim()} style={{ background: '#bb9af7', border: 'none', borderRadius: 'var(--border-radius)', padding: '10px 16px', color: '#1a1b26', fontWeight: 600, cursor: 'pointer', opacity: (sending || !input.trim()) ? 0.5 : 1 }}>{'\u2191'}</button>
        </div>
      </div>
    </div>
  );
}
